import { MOCK_MONTHLY_TREND } from "@/mocks/ledger";
import { getGroupView, listGroups } from "@/services/groups";
import { listMembers } from "@/services/members";
import { buildAllDemands, listReceipts } from "@/services/collections";
import { listPayouts } from "@/services/payouts";
import { listAuctions } from "@/services/auctions";
import { USE_MOCK_API } from "@/services/config";
import { http } from "@/services/http";
import {
  type AgeingBucket,
  type AuctionHistoryRow,
  type CollectionDemand,
  type DashboardData,
  type EarningsReport,
  type GroupEarningRow,
  type GroupPerformanceReport,
  type KametiGroup,
  type Member,
  type MemberRemark,
  type OutstandingReport,
  type OutstandingRow,
  type PaymentMode,
  type PayoutView,
  type ReceiptView,
} from "@/services/types";
import { cyclePeriodLabel } from "@/utils/group";

/**
 * Centralized reports service (Phase 6). Every report screen calls through here —
 * no screen performs a direct data fetch. Reports are read-only: they aggregate
 * the same demand, receipt, payout, auction and group data the operational
 * modules recorded, so every figure reconciles against Phases 2–5.
 */

const delay = (ms = 300) => new Promise((resolve) => setTimeout(resolve, ms));

/* ================= Real API — backend shapes and mapping ================= */

const PAYMENT_MODE_FROM_BACKEND: Record<string, PaymentMode> = {
  CASH: "cash",
  UPI: "upi",
  BANK_TRANSFER: "bank_transfer",
  CHEQUE: "cheque",
  MEMBER_ACCOUNT: "member_account",
};
const PAYMENT_MODE_TO_BACKEND: Record<string, string> = {
  cash: "CASH",
  upi: "UPI",
  bank_transfer: "BANK_TRANSFER",
  cheque: "CHEQUE",
  member_account: "MEMBER_ACCOUNT",
};

/** Local "YYYY-MM-DD" — matches DatePicker.tsx / collections.ts's own fix; a string already in this shape passes through untouched. */
function toDateOnlyString(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface BackendReceiptLine {
  groupCode: string;
  groupName: string;
  cycle: number;
  periodLabel: string;
  amount: string;
}

interface BackendReceiptRow {
  id: string;
  receiptNo: string;
  memberId: string;
  memberCode: string;
  memberName: string;
  memberPhone: string;
  total: string;
  mode: string;
  referenceNo: string | null;
  receivedOn: string;
  remarks: string | null;
  collectedBy: string;
  isReversed: boolean;
  reversalReason: string | null;
  createdAt: string;
  lines: BackendReceiptLine[];
  remainingBalance: string;
}

function mapReceiptRow(r: BackendReceiptRow): ReceiptView {
  return {
    id: r.id,
    receiptNumber: r.receiptNo,
    memberId: r.memberId,
    payments: [],
    total: Number(r.total),
    mode: PAYMENT_MODE_FROM_BACKEND[r.mode] ?? "cash",
    reference: r.referenceNo ?? "",
    receivedOn: r.receivedOn,
    remarks: r.remarks ?? "",
    collectedBy: r.collectedBy,
    createdAt: r.createdAt,
    reversedAt: r.isReversed ? r.createdAt : null,
    reversalReason: r.reversalReason,
    memberName: r.memberName,
    memberCode: r.memberCode,
    memberPhone: r.memberPhone,
    lines: r.lines.map((l) => ({ groupCode: l.groupCode, groupName: l.groupName, cycle: l.cycle, periodLabel: l.periodLabel, amount: Number(l.amount) })),
    remainingBalance: Number(r.remainingBalance),
  };
}

interface BackendCollectionRegister {
  rows: BackendReceiptRow[];
  modeTotals: Record<string, string>;
  grandTotal: string;
  staff: string[];
}

interface BackendOutstandingDemand {
  id: string;
  groupId: string;
  groupCode: string;
  groupName: string;
  cycle: number;
  periodLabel: string;
  memberId: string;
  memberCode: string;
  memberName: string;
  memberPhone: string;
  ticketNumber: string;
  baseInstallment: string;
  dividendCredit: string;
  lateFee: string;
  netPayable: string;
  paid: string;
  balance: string;
  status: "pending" | "partial" | "paid";
  dueDate: string;
  daysOverdue: number;
  applicableLateFee: number;
  hasLateFeeApplied: boolean;
  wonThisCycle: boolean;
  groupMemberId: string;
}

function mapOutstandingDemand(d: BackendOutstandingDemand): CollectionDemand {
  return {
    id: d.id,
    groupId: d.groupId,
    groupCode: d.groupCode,
    groupName: d.groupName,
    cycle: d.cycle,
    periodLabel: d.periodLabel,
    memberId: d.memberId,
    memberCode: d.memberCode,
    memberName: d.memberName,
    memberPhone: d.memberPhone,
    ticketNumber: d.ticketNumber,
    baseInstallment: Number(d.baseInstallment),
    dividendCredit: Number(d.dividendCredit),
    lateFee: Number(d.lateFee),
    netPayable: Number(d.netPayable),
    paid: Number(d.paid),
    balance: Number(d.balance),
    status: d.status,
    dueDate: d.dueDate,
    daysOverdue: d.daysOverdue,
    applicableLateFee: d.applicableLateFee,
    hasLateFeeApplied: d.hasLateFeeApplied,
    wonThisCycle: d.wonThisCycle,
    groupMemberId: d.groupMemberId,
  };
}

interface BackendOutstandingRow {
  memberId: string;
  memberName: string;
  memberPhone: string;
  totalOutstanding: string;
  demands: BackendOutstandingDemand[];
  buckets: Record<AgeingBucket, string>;
}

interface BackendOutstandingReport {
  rows: BackendOutstandingRow[];
  flat: BackendOutstandingDemand[];
  bucketTotals: Record<AgeingBucket, string>;
  grandTotal: string;
}

interface BackendAuctionHistoryRow {
  groupId: string;
  groupCode: string;
  groupName: string;
  cycle: number;
  periodLabel: string;
  scheduledDate: string | null;
  winnerMemberId: string | null;
  winnerName: string;
  winnerTicket: string;
  pool: string;
  winningBid: string | null;
  discount: string;
  discountPct: number;
  perMemberDividend: string;
  isOrganizerReserved: boolean;
}

interface BackendAuctionHistoryData {
  rows: BackendAuctionHistoryRow[];
  avgDiscount: string;
  avgDiscountPct: number;
}

interface BackendGroupEarningRow {
  groupId: string;
  groupCode: string;
  groupName: string;
  reservedCycle: number;
  reservedCyclePassed: boolean;
  reservedPool: string;
  commission: string;
  totalEarning: string;
  commissionType: string;
}

interface BackendEarningsReport {
  groups: BackendGroupEarningRow[];
  totalReserved: string;
  totalCommission: string;
  totalEarning: string;
}

interface BackendGroupPerformanceCycle {
  cycle: number;
  periodLabel: string;
  winnerName: string;
  winningBid: string;
  discount: string;
  perMemberDividend: string;
  status: "pending" | "completed";
  collected: string;
  expected: string;
  collectionPct: number;
}

interface BackendGroupPerformanceMember {
  groupMemberId: string;
  enrollmentStatus: string;
  memberId: string;
  memberCode: string;
  memberName: string;
  memberPhone?: string;
  ticketNumber: string;
  wonCycle: number | null;
  outstanding: string;
  remarks?: MemberRemark[];
}

interface BackendGroupPerformanceReport {
  group: {
    groupId: string;
    groupCode: string;
    name: string;
    monthlyInstallment: string;
    memberCount: number;
    chitValue: string;
    startDate: string;
    auctionDay: number;
    reservedCycle: number;
    status: string;
  };
  cycles: BackendGroupPerformanceCycle[];
  members: BackendGroupPerformanceMember[];
  totalCollected: string;
  totalExpected: string;
  currentOutstanding: string;
}

interface BackendDashboardData {
  activeGroups: number;
  totalMembers: number;
  monthCollection: string;
  pendingOverdue: string;
  trend: { month: string; collections: string; payouts: string }[];
  groupPerformance: { name: string; collected: string; expected: string; pct: number }[];
  upcomingAuctions: { id: string; groupId: string; groupCode: string; groupName: string; cycle: number; periodLabel: string; scheduledDate: string | null }[];
  recentCollections: { id: string; receiptNumber: string; memberName: string; total: string; receivedOn: string; reversedAt: string | null; lines: { groupName: string; cycle: number; periodLabel: string }[] }[];
  ageing: { bucket: AgeingBucket; amount: string }[];
}

export interface CollectionRegisterData {
  rows: ReceiptView[];
  modeTotals: Record<PaymentMode, number>;
  grandTotal: number;
  staff: string[];
}

/** Collection register — all receipts in a range with mode-wise subtotals. */
export async function getCollectionRegister(options: {
  range?: { start?: string | null; end?: string | null };
  groupId?: string;
  mode?: string;
  collectedBy?: string;
}): Promise<CollectionRegisterData> {
  if (!USE_MOCK_API) {
    const params = new URLSearchParams();
    if (options.range?.start) params.set("dateFrom", toDateOnlyString(options.range.start));
    if (options.range?.end) params.set("dateTo", toDateOnlyString(options.range.end));
    if (options.groupId) params.set("groupId", options.groupId);
    if (options.mode) params.set("mode", PAYMENT_MODE_TO_BACKEND[options.mode] ?? options.mode.toUpperCase());
    if (options.collectedBy) params.set("collectedBy", options.collectedBy);
    const data = await http<BackendCollectionRegister>(`/reports/collection-register?${params.toString()}`, { method: "GET" });
    const modeTotals: Record<PaymentMode, number> = { cash: 0, upi: 0, bank_transfer: 0, cheque: 0, member_account: 0 };
    for (const [backendMode, total] of Object.entries(data.modeTotals)) {
      const mode = PAYMENT_MODE_FROM_BACKEND[backendMode];
      if (mode) modeTotals[mode] = Number(total);
    }
    return { rows: data.rows.map(mapReceiptRow), modeTotals, grandTotal: Number(data.grandTotal), staff: data.staff };
  }

  await delay();
  const receipts = await listReceipts(options.range);
  let rows = receipts;
  if (options.groupId) {
    const groups = await listGroups();
    const codes = new Set(groups.filter((g) => g.id === options.groupId).map((g) => g.groupCode));
    rows = rows.filter((r) => r.lines.some((l) => codes.has(l.groupCode)));
  }
  if (options.mode) rows = rows.filter((r) => r.mode === options.mode);
  if (options.collectedBy) rows = rows.filter((r) => r.collectedBy === options.collectedBy);

  const modeTotals: Record<PaymentMode, number> = { cash: 0, upi: 0, bank_transfer: 0, cheque: 0, member_account: 0 };
  for (const r of rows) {
    if (r.reversedAt) continue;
    modeTotals[r.mode] = Math.round((modeTotals[r.mode] + r.total) * 100) / 100;
  }
  const grandTotal = Math.round(Object.values(modeTotals).reduce((s, v) => s + v, 0) * 100) / 100;
  const staff = Array.from(new Set(receipts.map((r) => r.collectedBy))).sort();

  return { rows, modeTotals, grandTotal, staff };
}

const AGEING_LABEL: Record<AgeingBucket, string> = {
  not_due: "Not yet due",
  b1_30: "1–30 days",
  b31_60: "31–60 days",
  b61_90: "61–90 days",
  b90_plus: "Over 90 days",
};

function ageingBucket(daysOverdue: number): AgeingBucket {
  if (daysOverdue <= 0) return "not_due";
  if (daysOverdue <= 30) return "b1_30";
  if (daysOverdue <= 60) return "b31_60";
  if (daysOverdue <= 90) return "b61_90";
  return "b90_plus";
}

export { AGEING_LABEL };

/** Outstanding & defaulter report with ageing buckets, grouped by member. */
export async function getOutstandingReport(): Promise<OutstandingReport> {
  if (!USE_MOCK_API) {
    const data = await http<BackendOutstandingReport>("/reports/outstanding", { method: "GET" });
    const bucketTotals = Object.fromEntries(Object.entries(data.bucketTotals).map(([k, v]) => [k, Number(v)])) as Record<AgeingBucket, number>;
    return {
      rows: data.rows.map((r) => ({
        memberId: r.memberId,
        memberName: r.memberName,
        memberPhone: r.memberPhone,
        totalOutstanding: Number(r.totalOutstanding),
        demands: r.demands.map(mapOutstandingDemand),
        buckets: Object.fromEntries(Object.entries(r.buckets).map(([k, v]) => [k, Number(v)])) as Record<AgeingBucket, number>,
      })),
      flat: data.flat.map(mapOutstandingDemand),
      bucketTotals,
      grandTotal: Number(data.grandTotal),
    };
  }

  await delay();
  const demands = (await buildAllDemands()).filter((d) => d.balance > 0);

  const bucketTotals: Record<AgeingBucket, number> = { not_due: 0, b1_30: 0, b31_60: 0, b61_90: 0, b90_plus: 0 };
  for (const d of demands) bucketTotals[ageingBucket(d.daysOverdue)] += d.balance;

  const byMember = new Map<string, OutstandingRow>();
  for (const d of demands) {
    const row = byMember.get(d.memberId) ?? {
      memberId: d.memberId,
      memberName: d.memberName,
      memberPhone: d.memberPhone,
      totalOutstanding: 0,
      demands: [],
      buckets: { not_due: 0, b1_30: 0, b31_60: 0, b61_90: 0, b90_plus: 0 },
    };
    row.demands.push(d);
    row.totalOutstanding += d.balance;
    row.buckets[ageingBucket(d.daysOverdue)] += d.balance;
    byMember.set(d.memberId, row);
  }
  const rows = Array.from(byMember.values()).sort((a, b) => b.totalOutstanding - a.totalOutstanding);
  const grandTotal = rows.reduce((s, r) => s + r.totalOutstanding, 0);

  return {
    rows,
    flat: [...demands].sort((a, b) => b.daysOverdue - a.daysOverdue),
    bucketTotals,
    grandTotal: Math.round(grandTotal * 100) / 100,
  };
}

export interface AuctionHistoryData {
  rows: AuctionHistoryRow[];
  avgDiscount: number;
  avgDiscountPct: number;
}

/** Auction history report across all closed auctions. */
export async function getAuctionHistory(options: {
  range?: { start?: string | null; end?: string | null };
  groupId?: string;
}): Promise<AuctionHistoryData> {
  if (!USE_MOCK_API) {
    const params = new URLSearchParams();
    if (options.range?.start) params.set("dateFrom", toDateOnlyString(options.range.start));
    if (options.range?.end) params.set("dateTo", toDateOnlyString(options.range.end));
    if (options.groupId) params.set("groupId", options.groupId);
    const data = await http<BackendAuctionHistoryData>(`/reports/auction-history?${params.toString()}`, { method: "GET" });
    return {
      rows: data.rows.map((r) => ({
        groupId: r.groupId,
        groupCode: r.groupCode,
        groupName: r.groupName,
        cycle: r.cycle,
        periodLabel: r.periodLabel,
        scheduledDate: r.scheduledDate ?? "",
        winnerMemberId: r.winnerMemberId ?? "",
        winnerName: r.winnerName,
        winnerTicket: r.winnerTicket,
        pool: Number(r.pool),
        winningBid: Number(r.winningBid ?? 0),
        discount: Number(r.discount),
        discountPct: r.discountPct,
        perMemberDividend: Number(r.perMemberDividend),
        isOrganizerReserved: r.isOrganizerReserved,
      })),
      avgDiscount: Number(data.avgDiscount),
      avgDiscountPct: data.avgDiscountPct,
    };
  }

  await delay();
  const [auctions, groups, members] = await Promise.all([listAuctions(), listGroups(), listMembers()]);
  const memberMap = new Map(members.map((m) => [m.id, m]));
  const groupMap = new Map(groups.map((g) => [g.id, g]));

  const rows: AuctionHistoryRow[] = [];
  for (const a of auctions) {
    if (a.status !== "closed" || !a.result) continue;
    if (options.groupId && a.groupId !== options.groupId) continue;
    if (options.range?.start && new Date(a.scheduledDate) < new Date(options.range.start)) continue;
    if (options.range?.end) {
      const end = new Date(options.range.end as string);
      end.setHours(23, 59, 59, 999);
      if (new Date(a.scheduledDate) > end) continue;
    }
    const group = groupMap.get(a.groupId);
    const winner = memberMap.get(a.result.winnerMemberId);
    const ticket = group?.tickets.find((t) => t.memberId === a.result.winnerMemberId);
    rows.push({
      groupId: a.groupId,
      groupCode: a.groupCode,
      groupName: a.groupName,
      cycle: a.cycle,
      periodLabel: a.periodLabel,
      scheduledDate: a.scheduledDate,
      winnerMemberId: a.result.winnerMemberId,
      winnerName: winner?.fullName ?? "—",
      winnerTicket: ticket?.ticketNumber ?? "—",
      pool: a.pool,
      winningBid: a.result.winningBid,
      discount: a.result.discount,
      discountPct: a.pool > 0 ? (a.result.discount / a.pool) * 100 : 0,
      perMemberDividend: a.result.perMemberDividend,
      isOrganizerReserved: a.isOrganizerReserved,
    });
  }
  rows.sort((x, y) => x.scheduledDate.localeCompare(y.scheduledDate));
  const avgDiscount = rows.length ? rows.reduce((s, r) => s + r.discount, 0) / rows.length : 0;
  const avgDiscountPct = rows.length ? rows.reduce((s, r) => s + r.discountPct, 0) / rows.length : 0;

  return {
    rows,
    avgDiscount: Math.round(avgDiscount * 100) / 100,
    avgDiscountPct: Math.round(avgDiscountPct * 10) / 10,
  };
}

export interface PayoutRegisterData {
  rows: PayoutView[];
  totals: { prize: number; dues: number; netPayable: number; netPaid: number };
}

/** Payout register — all payouts in a range with a totals row. */
export async function getPayoutRegister(options: {
  range?: { start?: string | null; end?: string | null };
  groupId?: string;
  status?: string;
  mode?: string;
}): Promise<PayoutRegisterData> {
  if (!USE_MOCK_API) {
    // Reuses payouts.ts's own listPayouts() rather than re-deriving PayoutView
    // from a second, leaner backend shape — one source of truth for what a
    // payout row looks like. That endpoint doesn't support a date range
    // itself, so it's applied client-side here exactly as the mock does.
    let rows = await listPayouts({ groupId: options.groupId, status: options.status, mode: options.mode });
    if (options.range?.start) rows = rows.filter((p) => new Date(p.createdAt) >= new Date(options.range!.start as string));
    if (options.range?.end) {
      const end = new Date(options.range.end as string);
      end.setHours(23, 59, 59, 999);
      rows = rows.filter((p) => new Date(p.createdAt) <= end);
    }
    const active = rows.filter((p) => p.status !== "reversed");
    return {
      rows,
      totals: {
        prize: Math.round(active.reduce((s, p) => s + p.prize, 0) * 100) / 100,
        dues: Math.round(active.reduce((s, p) => s + p.sameGroupDuesTotal, 0) * 100) / 100,
        netPayable: Math.round(active.reduce((s, p) => s + p.netPayable, 0) * 100) / 100,
        netPaid: Math.round(active.reduce((s, p) => s + p.amountPaid, 0) * 100) / 100,
      },
    };
  }

  await delay();
  let rows = await listPayouts();
  if (options.groupId) rows = rows.filter((p) => p.groupId === options.groupId);
  if (options.status) rows = rows.filter((p) => p.status === options.status);
  if (options.mode) rows = rows.filter((p) => p.disbursements.some((d) => d.mode === options.mode));
  if (options.range?.start) rows = rows.filter((p) => new Date(p.createdAt) >= new Date(options.range.start as string));
  if (options.range?.end) {
    const end = new Date(options.range.end as string);
    end.setHours(23, 59, 59, 999);
    rows = rows.filter((p) => new Date(p.createdAt) <= end);
  }
  const active = rows.filter((p) => p.status !== "reversed");
  return {
    rows,
    totals: {
      prize: Math.round(active.reduce((s, p) => s + p.prize, 0) * 100) / 100,
      dues: Math.round(active.reduce((s, p) => s + p.sameGroupDuesTotal, 0) * 100) / 100,
      netPayable: Math.round(active.reduce((s, p) => s + p.netPayable, 0) * 100) / 100,
      netPaid: Math.round(active.reduce((s, p) => s + p.amountPaid, 0) * 100) / 100,
    },
  };
}

/** Organizer earnings — reserved full-pool cycles plus any configured commission. */
export async function getEarningsReport(): Promise<EarningsReport> {
  if (!USE_MOCK_API) {
    const data = await http<BackendEarningsReport>("/reports/earnings", { method: "GET" });
    return {
      groups: data.groups.map((g) => ({
        groupId: g.groupId,
        groupCode: g.groupCode,
        groupName: g.groupName,
        reservedCycle: g.reservedCycle,
        reservedCyclePassed: g.reservedCyclePassed,
        reservedPool: Number(g.reservedPool),
        commission: Number(g.commission),
        totalEarning: Number(g.totalEarning),
      })),
      totalReserved: Number(data.totalReserved),
      totalCommission: Number(data.totalCommission),
      totalEarning: Number(data.totalEarning),
    };
  }

  await delay();
  const groups = (await listGroups()).filter((g) => g.status === "active" || g.status === "completed");
  const rows: GroupEarningRow[] = [];
  for (const g of groups) {
    const reserved = g.wonCycles.find((w) => w.cycle === g.reservedCycle && w.memberId === g.organizerMemberId);
    const reservedPool = reserved ? reserved.winningBid : 0;
    const commission = g.commissionType === "none"
      ? 0
      : g.commissionType === "fixed"
        ? g.commissionValue
        : g.commissionType === "pool_percentage"
          ? (g.chitValue * g.commissionValue) / 100
          : 0; // discount_percentage on a reserved cycle (discount 0) → 0
    rows.push({
      groupId: g.id,
      groupCode: g.groupCode,
      groupName: g.name,
      reservedCycle: g.reservedCycle,
      reservedCyclePassed: !!reserved,
      reservedPool,
      commission: Math.round(commission * 100) / 100,
      totalEarning: Math.round((reservedPool + commission) * 100) / 100,
    });
  }
  return {
    groups: rows,
    totalReserved: Math.round(rows.reduce((s, r) => s + r.reservedPool, 0) * 100) / 100,
    totalCommission: Math.round(rows.reduce((s, r) => s + r.commission, 0) * 100) / 100,
    totalEarning: Math.round(rows.reduce((s, r) => s + r.totalEarning, 0) * 100) / 100,
  };
}

/** Group performance — configuration, per-cycle results, collection and members. */
export async function getGroupPerformance(groupId: string): Promise<GroupPerformanceReport> {
  if (!USE_MOCK_API) {
    const data = await http<BackendGroupPerformanceReport>(`/reports/group-performance/${groupId}`, { method: "GET" });
    return {
      group: {
        groupId: data.group.groupId,
        groupCode: data.group.groupCode,
        name: data.group.name,
        monthlyInstallment: Number(data.group.monthlyInstallment),
        memberCount: data.group.memberCount,
        chitValue: Number(data.group.chitValue),
        startDate: data.group.startDate,
        auctionDay: data.group.auctionDay,
        reservedCycle: data.group.reservedCycle,
        status: (data.group.status.toLowerCase() as GroupPerformanceReport["group"]["status"]),
      },
      cycles: data.cycles.map((c) => ({
        cycle: c.cycle,
        periodLabel: c.periodLabel,
        winnerName: c.winnerName,
        winningBid: Number(c.winningBid),
        discount: Number(c.discount),
        perMemberDividend: Number(c.perMemberDividend),
        status: c.status,
        collected: Number(c.collected),
        expected: Number(c.expected),
        collectionPct: c.collectionPct,
      })),
      members: data.members.map((m) => ({
        groupMemberId: m.groupMemberId,
        isActiveEnrollment: (m.enrollmentStatus ?? "ACTIVE") === "ACTIVE",
        remarks: m.remarks ?? [],
        memberId: m.memberId,
        memberCode: m.memberCode,
        memberName: m.memberName,
        memberPhone: m.memberPhone ?? "",
        ticketNumber: m.ticketNumber,
        wonCycle: m.wonCycle,
        outstanding: Number(m.outstanding),
      })),
      totalCollected: Number(data.totalCollected),
      totalExpected: Number(data.totalExpected),
      currentOutstanding: Number(data.currentOutstanding),
    };
  }

  await delay();
  const [view, demands, members] = await Promise.all([getGroupView(groupId), buildAllDemands(), listMembers()]);
  const memberMap = new Map(members.map((m) => [m.id, m]));

  const cycles = view.cycles.map((c) => {
    const cycleDemands = demands.filter((d) => d.groupId === groupId && d.cycle === c.cycle);
    const collected = cycleDemands.reduce((s, d) => s + d.paid, 0);
    const expected = cycleDemands.reduce((s, d) => s + d.netPayable, 0);
    const winnerName = c.winnerMemberId ? memberMap.get(c.winnerMemberId)?.fullName ?? "—" : "—";
    return {
      cycle: c.cycle,
      periodLabel: c.periodLabel,
      winnerName,
      winningBid: c.winningBid,
      discount: c.discount,
      perMemberDividend: c.perMemberDividend,
      status: c.status,
      collected: Math.round(collected * 100) / 100,
      expected: Math.round(expected * 100) / 100,
      collectionPct: expected > 0 ? Math.round((collected / expected) * 1000) / 10 : 0,
    };
  });

  const memberRows = view.tickets
    .filter((t) => t.memberId)
    .map((t) => {
      const m = memberMap.get(t.memberId);
      const outstanding = demands
        .filter((d) => d.groupId === groupId && d.memberId === t.memberId)
        .reduce((s, d) => s + d.balance, 0);
      return {
        memberId: t.memberId,
        memberCode: m?.memberCode ?? "—",
        memberName: m?.fullName ?? "—",
        memberPhone: m?.primaryPhone ?? "",
        ticketNumber: t.ticketNumber,
        wonCycle: t.wonCycle ?? null,
        outstanding: Math.round(outstanding * 100) / 100,
      };
    });

  const totalExpected = cycles.reduce((s, c) => s + c.expected, 0);
  const totalCollected = cycles.reduce((s, c) => s + c.collected, 0);
  const currentOutstanding = Math.round(memberRows.reduce((s, m) => s + m.outstanding, 0) * 100) / 100;

  return {
    group: {
      groupId: view.id,
      groupCode: view.groupCode,
      name: view.name,
      monthlyInstallment: view.monthlyInstallment,
      memberCount: view.memberCount,
      chitValue: view.chitValue,
      startDate: view.startDate,
      auctionDay: view.auctionDay,
      reservedCycle: view.reservedCycle,
      status: view.status,
    },
    cycles,
    members: memberRows,
    totalCollected: Math.round(totalCollected * 100) / 100,
    totalExpected: Math.round(totalExpected * 100) / 100,
    currentOutstanding,
  };
}

/** Aggregated data for the live dashboard. */
export async function getDashboardData(): Promise<DashboardData> {
  if (!USE_MOCK_API) {
    const data = await http<BackendDashboardData>("/reports/dashboard", { method: "GET" });
    return {
      activeGroups: data.activeGroups,
      totalMembers: data.totalMembers,
      monthCollection: Number(data.monthCollection),
      pendingOverdue: Number(data.pendingOverdue),
      trend: data.trend.map((t) => ({ month: t.month, collections: Number(t.collections), payouts: Number(t.payouts) })),
      groupPerformance: data.groupPerformance.map((g) => ({ name: g.name, collected: Number(g.collected), expected: Number(g.expected), pct: g.pct })),
      upcomingAuctions: data.upcomingAuctions.map((a) => ({
        id: a.id,
        groupId: a.groupId,
        groupName: a.groupName,
        cycle: a.cycle,
        periodLabel: a.periodLabel,
        scheduledDate: a.scheduledDate ?? "",
      })),
      recentCollections: data.recentCollections.map((r) => ({
        id: r.id,
        receiptNumber: r.receiptNumber,
        memberName: r.memberName,
        total: Number(r.total),
        receivedOn: r.receivedOn,
        reversedAt: r.reversedAt,
        lines: r.lines.map((l) => ({ groupName: l.groupName })),
      })),
      ageing: data.ageing.map((e) => ({ bucket: e.bucket, label: AGEING_LABEL[e.bucket], amount: Number(e.amount) })),
    };
  }

  await delay();
  const [groups, members, demands, receipts, auctions] = await Promise.all([
    listGroups(),
    listMembers(),
    buildAllDemands(),
    listReceipts(),
    listAuctions(),
  ]);
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

  const activeGroups = groups.filter((g) => g.status === "active").length;
  const totalMembers = members.length;
  const monthCollection = receipts
    .filter((r) => !r.reversedAt && new Date(r.receivedOn) >= monthStart)
    .reduce((s, r) => s + r.total, 0);
  const pendingOverdue = demands.filter((d) => d.balance > 0).reduce((s, d) => s + d.balance, 0);

  const groupPerformance = groups
    .filter((g) => g.status === "active")
    .map((g) => {
      const latest = [...g.wonCycles].sort((a, b) => a.cycle - b.cycle)[g.wonCycles.length - 1];
      const cycleDemands = latest ? demands.filter((d) => d.groupId === g.id && d.cycle === latest.cycle) : [];
      const collected = cycleDemands.reduce((s, d) => s + d.paid, 0);
      const expected = cycleDemands.reduce((s, d) => s + d.netPayable, 0);
      return {
        name: g.name,
        collected: Math.round(collected * 100) / 100,
        expected: Math.round(expected * 100) / 100,
        pct: expected > 0 ? Math.round((collected / expected) * 100) : 0,
      };
    })
    .sort((a, b) => b.collected - a.collected)
    .slice(0, 8);

  const upcomingAuctions = auctions
    .filter((a) => a.status === "pending")
    .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate))
    .slice(0, 6);

  const recentCollections = [...receipts].sort((a, b) => b.receivedOn.localeCompare(a.receivedOn)).slice(0, 6);

  const ageing = (["not_due", "b1_30", "b31_60", "b61_90", "b90_plus"] as AgeingBucket[]).map((b) => {
    const amount = demands.filter((d) => d.balance > 0 && ageingBucket(d.daysOverdue) === b).reduce((s, d) => s + d.balance, 0);
    return { bucket: b, label: AGEING_LABEL[b], amount: Math.round(amount * 100) / 100 };
  });

  return {
    activeGroups,
    totalMembers,
    monthCollection: Math.round(monthCollection * 100) / 100,
    pendingOverdue: Math.round(pendingOverdue * 100) / 100,
    trend: MOCK_MONTHLY_TREND,
    groupPerformance,
    upcomingAuctions,
    recentCollections,
    ageing,
  };
}

export const reportsService = {
  getCollectionRegister,
  getOutstandingReport,
  getAuctionHistory,
  getPayoutRegister,
  getEarningsReport,
  getGroupPerformance,
  getDashboardData,
};

export type { KametiGroup, Member, CollectionDemand, PaymentMode };